// ── Types & constants shared across the ScopeMigration module ──────────────
import { systemConfig } from '@/config/systemConfig';
import type { WorkflowScope } from '../../api/scopesApi';

export const SCOPE_KEY_LIMIT = systemConfig.scope_migration.scope_key_limit;

export type MigrationStep = 'select' | 'preview' | 'done';

/** Shape returned by `computePreviewSummary`. */
export interface PreviewSummary {
  rootCount: number;
  rootDisplays: string[];
  rootNs: string;
  childCount: number;
  children: WorkflowScope[];
  totalDescendants: number;
  treeDepth: number;
  distinctNamespaces: string[];
  namespaceCounts: { ns: string; count: number }[];
  totalScopes: number;
  /** Number of scopes that have at least one hot-stream hit. */
  scopesWithHotStream: number;
  /** Estimated outcome register count (scope-count heuristic until server-side is available). */
  estimatedOutcomeCount?: number;
  /** Estimated hot-stream register count (scope-count heuristic until server-side is available). */
  estimatedHotStreamCount?: number;
  /** Number of GCS artifact files referenced by outcomes. */
  artifactFileCount: number;
  /** Total size of all artifact files in bytes (0 if not enriched). */
  artifactTotalBytes: number;
  /** Server-side preview counts (populated asynchronously after preview opens). */
  serverCounts?: {
    outcomeCount: number;
    hotStreamCount: number;
    gcsFileCount: number;
    archiveFileCount: number;
  };
}

/** Shape returned by the `exportSummary` memo. */
export interface ExportSummary {
  scopeCount: number;
  namespaceCount: number;
  namespaces: string[];
  outcomeCount: number;
  handlerCount: number;
  handlers: string[];
  hotStreamCount: number;
  archiveFileCount: number;
  /** GCS artifact files referenced by outcomes + hot archives. */
  artifactFileCount: number;
  /** Total bytes of all artifacts (0 if enrichment not available). */
  artifactTotalBytes: number;
}

// Re-export API types used across the module
export type { WorkflowScope, ScopeNamespaceCount } from '../../api/scopesApi';
export type {
  ScopeManifest,
  ManifestScope,
  ManifestOutcome,
  ExportParams,
  ImportResult,
  ConflictStrategy,
  PendingImportItem,
  PendingImportDetail,
  QueueResult,
  ExportPreviewCounts,
  PreviewExportParams,
} from '../../api/scopeMigrateApi';
export type { TenantPickerEntry as Tenant } from '@/features/system/iam-admin/api/iamAdminApi';
export type { EnvironmentDef } from '@/config/systemConfig';
